import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Paperclip, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUSES = [
    { id: "novo", label: "Novo", cls: "bg-brand text-white" },
    { id: "em-curso", label: "Em curso", cls: "bg-ember text-ink" },
    { id: "concluido", label: "Concluído", cls: "bg-zinc-600 text-white" },
];

const statusOf = (r) => STATUSES.find((s) => s.id === (r.status || "novo")) || STATUSES[0];

export const RequestsPanel = ({ auth }) => {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("todos");
    const [busy, setBusy] = useState("");

    const load = async () => {
        try {
            const r = await axios.get(`${API}/admin/quote-requests`, auth());
            setItems(r.data.items || []);
        } catch {
            /* sem ligação */
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setStatus = async (id, status) => {
        setBusy(id);
        try {
            await axios.patch(`${API}/admin/quote-requests/${id}`, { status }, auth());
            await load();
            toast.success("Estado atualizado.");
        } catch {
            toast.error("Falha ao atualizar o estado.");
        } finally {
            setBusy("");
        }
    };

    const remove = async (id) => {
        setBusy(id);
        try {
            await axios.delete(`${API}/admin/quote-requests/${id}`, auth());
            await load();
            toast.success("Pedido eliminado.");
        } catch {
            toast.error("Falha ao eliminar o pedido.");
        } finally {
            setBusy("");
        }
    };

    const filtered =
        filter === "todos" ? items : items.filter((i) => (i.status || "novo") === filter);

    return (
        <div className="mt-14" data-testid="requests-panel">
            <div className="flex flex-wrap gap-2" data-testid="requests-filters">
                {[
                    ["todos", `Todos (${items.length})`],
                    ...STATUSES.map((s) => [
                        s.id,
                        `${s.label} (${items.filter((i) => (i.status || "novo") === s.id).length})`,
                    ]),
                ].map(([id, label]) => (
                    <button
                        key={id}
                        onClick={() => setFilter(id)}
                        data-testid={`requests-filter-${id}`}
                        className={`border px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
                            filter === id
                                ? "border-brand bg-brand text-white"
                                : "border-white/15 text-zinc-400 hover:border-white/40 hover:text-white"
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <p className="mt-10 border border-dashed border-white/15 p-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
                    Sem pedidos neste estado
                </p>
            ) : (
                <div className="mt-8 space-y-5">
                    {filtered.map((r) => {
                        const st = statusOf(r);
                        return (
                            <div
                                key={r.id}
                                className="border border-white/10 bg-coal p-6"
                                data-testid={`request-card-${r.id}`}
                            >
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <p className="font-display text-lg font-bold uppercase tracking-tight">
                                            {r.nome}
                                            {r.empresa && (
                                                <span className="font-normal text-zinc-500">
                                                    {" "}
                                                    — {r.empresa}
                                                </span>
                                            )}
                                        </p>
                                        <p className="mt-1 font-mono text-[11px] text-zinc-500">
                                            {r.email}
                                            {r.telefone && ` · ${r.telefone}`}
                                        </p>
                                    </div>
                                    <span
                                        className={`px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] ${st.cls}`}
                                        data-testid={`request-status-${r.id}`}
                                    >
                                        {st.label}
                                    </span>
                                </div>
                                <div className="mt-4 grid gap-2 text-sm text-zinc-400 sm:grid-cols-2">
                                    {r.tipo_servico && (
                                        <p>
                                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                                                Serviço:{" "}
                                            </span>
                                            {r.tipo_servico}
                                        </p>
                                    )}
                                    {(r.origem || r.destino) && (
                                        <p>
                                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                                                Trajeto:{" "}
                                            </span>
                                            {r.origem || "—"} → {r.destino || "—"}
                                        </p>
                                    )}
                                </div>
                                {r.mensagem && (
                                    <p className="mt-3 border-l-2 border-brand pl-4 text-sm leading-relaxed text-zinc-300">
                                        {r.mensagem}
                                    </p>
                                )}
                                <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-white/10 pt-4">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                                        {new Date(r.created_at).toLocaleString("pt-PT")}
                                    </span>
                                    {r.ficheiro && (
                                        <a
                                            href={`${BACKEND_URL}/api/uploads/${r.ficheiro}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`request-file-${r.id}`}
                                            className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400 transition-colors duration-300 hover:border-brand hover:text-brand"
                                        >
                                            <Paperclip className="h-3 w-3" />
                                            Anexo
                                        </a>
                                    )}
                                    <span className="flex-1" />
                                    {busy === r.id && (
                                        <Loader2 className="h-4 w-4 animate-spin text-brand" />
                                    )}
                                    {STATUSES.filter((s) => s.id !== (r.status || "novo")).map(
                                        (s) => (
                                            <button
                                                key={s.id}
                                                onClick={() => setStatus(r.id, s.id)}
                                                disabled={busy === r.id}
                                                data-testid={`request-set-${s.id}-${r.id}`}
                                                className="border border-white/15 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-300 transition-colors duration-300 hover:border-brand hover:text-brand disabled:opacity-50"
                                            >
                                                {s.label}
                                            </button>
                                        ),
                                    )}
                                    <button
                                        onClick={() => remove(r.id)}
                                        disabled={busy === r.id}
                                        data-testid={`request-delete-${r.id}`}
                                        className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 transition-colors duration-300 hover:border-brand hover:text-brand disabled:opacity-50"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                        Eliminar
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
